/**
 * 小窝场景的舞台（家园改版，c84a 收口单 FRONTEND-MUST-DELIVER 第 2 条：TA 有合理的位置、比例与接地感，状态变化看得见）。纯函数，不碰 DOM。
 *
 * 底图（家园第四包，c84a D0016-2、D0346-1、D0416-1）：r7k layer-split-v1 的完整原底图（屋内 853×1844、庭院 941×1672），
 * 加从同一张图里抠出来的透明遮挡片（屋内：茶几前沿、左前盆栽、右前盆栽；庭院：左前、右前栏杆植物）。
 * 底图与遮挡片共用同一个缩放、裁切（PLATES），遮挡片按原图像素偏移贴回原位；底图不是“干净地面”，家具、围栏都烘在里面，不挪。
 *
 * 坐标：舞台仍按宽 780、场景盒子显示第 50–1270 行的逻辑框算（FRAME）。每间的底图按宽铺满：
 * 原图 (x, y) → 舞台 (x × 780 ÷ 原图宽, 50 + (y − 裁切上沿) × 780 ÷ 原图宽)（toStage）。锚点、可走区、门口都先写原图像素，再换算。
 * 一个“站位点”是 TA 脚底落地的地方；画出来的大小按这一点离镜头远近（y 越大越近）定，越近越大。
 *
 * 位置只认后端的事实（`life.position.room / anchor / transition`，前端只读）：
 * - 锚点 → 画面上的一点（ANCHORS）。原图里没有窝、床、门和食盆位（r7k 坐标表 null）：窝 rest.1–4 落在地毯前沿和地板上，
 *   吃饭位挨着现有的独立食盆（USER-V4），电视、沙发这两个锚点只给一个合理的站位，不照锚点说物件。门窗的视觉中心不当脚底。
 * - 可走区（FLOOR）只取 r7k 估计的地板中央，并按 TA 的占地（脚底左右约 60–85、前后约 25 原图像素）往里收过；
 *   唯一的例外是茶几后面地毯后沿的一条（电视锚点），让 TA 站到茶几后面。茶几桌腿那一圈加上 TA 的占地是挡路的（BLOCKERS）：
 *   一段路穿过它时贴着它的角绕过去（routeAround），不穿桌腿、不上桌面。
 * - 醒着、手上没有具体在做的事：在自己这个点附近的一小块地方走走停停（wanderBox），隔几轮回到原点——只是画面上的走动，事实位置不变。
 * - 有转场（transition）：按真实起止时刻从一处走到另一处；跨房间时在这一间从门口离开、或从门口进来。
 *   屋里底图没有门：从画面左边走出去（地板一直铺到画外）；院子走到屋门台阶前，进门就不画了。
 */
import type { HomeActivityDetailKind, HomeLife, HomeRoom, HomeTransition, RhythmSky } from "@/shared/contracts";

export type StagePoint = { x: number; y: number };

/** 场景盒子显示的逻辑范围（行） */
export const FRAME = { width: 780, top: 50, bottom: 1270 } as const;

/** 站位点 → 场景盒子里的百分比 */
export function boxPct(p: StagePoint): { left: number; top: number } {
  return { left: (p.x / FRAME.width) * 100, top: ((p.y - FRAME.top) / (FRAME.bottom - FRAME.top)) * 100 };
}

/** 一张遮挡片：原图像素里的位置与大小；baseline 是它落地的那一行，TA 脚底比它靠后（y 更小）时画在 TA 上面 */
export type Occluder = { key: string; src: string; x: number; y: number; width: number; height: number; baseline: number };
/**
 * 一间屋的底图：原图大小、从哪一行开始进场景盒子、盒子下面接着铺的地面补色（原图最底 8 行整幅平均）、遮挡片；
 * motion 是和这张底图逐行对齐的环境微动视频（新底图还没有，不挂）。
 */
export type Plate = { src: string; width: number; height: number; cropTop: number; ground: string; occluders: readonly Occluder[]; motion?: string };

const LAYERS = "/ui-assets/HOME-LAYERS/v1/";
export const PLATES: Record<HomeRoom, Plate> = {
  inside: {
    src: `${LAYERS}room-plate.webp`,
    width: 853,
    height: 1844,
    // 上沿取第 150 行：窗、沙发、茶几、柜子在顶栏下面露出来，前景地板一直到左右前盆栽开始的地方
    cropTop: 150,
    ground: "#704e23",
    occluders: [
      // 茶几前桌腿落地约在第 805 行
      { key: "coffee-table", src: `${LAYERS}room-coffee-table-front.webp`, x: 385, y: 556, width: 272, height: 260, baseline: 805 },
      // 两盆前景盆栽在画面最前沿（落地在原图最底），可走区里的 TA 永远在它们后面
      { key: "left-planter", src: `${LAYERS}room-front-left-planter.webp`, x: 0, y: 1251, width: 218, height: 593, baseline: 1844 },
      { key: "right-planter", src: `${LAYERS}room-front-right-planter.webp`, x: 615, y: 1477, width: 238, height: 367, baseline: 1844 },
    ],
  },
  courtyard: {
    src: `${LAYERS}courtyard-plate.webp`,
    width: 941,
    height: 1672,
    // 上沿取第 0 行：屋门、台阶在顶栏下面露出来（信箱烘在顶栏后面，所以院子的信箱入口也放到左侧小图标）
    cropTop: 0,
    // 图到头以后补的地面色：铺地底边的暖沙色（底图第 1550 行中段平均 169,128,85）。原来取底图最底 8 行的平均 #615a20 偏橄榄（用户 10-03 不要深绿），
    // 后改深土色 #6a5232，在 390×844 下成了底部约 18% 的一条棕色带（2531 pre1530 走查，10-03）
    ground: "#a98055",
    occluders: [
      { key: "left-rail", src: `${LAYERS}courtyard-front-left-rail-plants.webp`, x: 0, y: 1100, width: 285, height: 572, baseline: 1672 },
      { key: "right-rail", src: `${LAYERS}courtyard-front-right-rail-plants.webp`, x: 580, y: 1180, width: 361, height: 492, baseline: 1672 },
    ],
  },
};

/** 原图一个像素在舞台上多大 */
export function plateScale(room: HomeRoom): number {
  return FRAME.width / PLATES[room].width;
}

/** 原图像素 → 舞台（取整） */
export function toStage(room: HomeRoom, p: StagePoint): StagePoint {
  const k = plateScale(room);
  return { x: Math.round(p.x * k), y: Math.round(FRAME.top + (p.y - PLATES[room].cropTop) * k) };
}

/** 场景盒子里露出的原图行数（盒子 390:610，按宽铺满） */
export function plateRows(room: HomeRoom): number {
  return (FRAME.bottom - FRAME.top) / plateScale(room);
}

/** 原图里一块矩形 → 场景盒子里的百分比（遮挡片用；和底图同一个缩放、裁切） */
export function plateRectPct(room: HomeRoom, r: { x: number; y: number; width: number; height: number }) {
  const plate = PLATES[room];
  const rows = plateRows(room);
  return {
    left: (r.x / plate.width) * 100,
    top: ((r.y - plate.cropTop) / rows) * 100,
    width: (r.width / plate.width) * 100,
    height: (r.height / rows) * 100,
  };
}

/** 场景里的一个框（CSS 像素，原点在场景左上） */
export type PxRect = { left: number; top: number; right: number; bottom: number };

/**
 * TA 头顶那一句会不会盖住桌上的相框（2531 R28 8.1：相框被状态牌盖住，I 10-03）：两框（各留 gap）上下、左右都重叠才算。
 * 会盖住就把这一句挂到 TA 脚下（HomeScene 写 data-below）——不左右挪：往左会压到左侧小图标，往右就离开 TA 头顶了。
 */
export function tagCovers(tag: PxRect, frame: PxRect, gap = 4): boolean {
  return tag.left < frame.right + gap && tag.right > frame.left - gap && tag.top < frame.bottom + gap && tag.bottom > frame.top - gap;
}

/** TA 脚底在这张遮挡片后面（比它落地那一行靠后）：遮挡片画在 TA 上面 */
export function isBehind(room: HomeRoom, foot: StagePoint, occluder: Occluder): boolean {
  return foot.y < toStage(room, { x: 0, y: occluder.baseline }).y;
}

const mapPoints = (room: HomeRoom, points: Record<string, StagePoint>) =>
  Object.fromEntries(Object.entries(points).map(([key, p]) => [key, toStage(room, p)]));

/** 各锚点脚底落在原图的哪一点（原图像素，见文件头） */
const PLATE_ANCHORS: Record<HomeRoom, Record<string, StagePoint>> = {
  inside: {
    // 窝：原图里没有窝和床，落在地毯前沿与地板上
    "inside.rest.1": { x: 520, y: 905 },
    "inside.rest.2": { x: 400, y: 1180 },
    "inside.rest.3": { x: 590, y: 960 },
    "inside.rest.4": { x: 460, y: 1010 },
    "inside.floor.1": { x: 560, y: 1060 },
    "inside.floor.2": { x: 590, y: 1280 },
    "inside.floor.3": { x: 420, y: 1090 },
    "inside.floor.4": { x: 520, y: 1230 },
    "inside.floor.5": { x: 380, y: 1300 },
    "inside.floor.6": { x: 560, y: 1350 },
    // 看窗外：窗在左上，站在左边地板上朝那边看
    "inside.window": { x: 370, y: 960 },
    // 茶几后面、地毯后沿（柜脚与桌腿之间那一条，见 FLOOR）：TA 站在这里时茶几前沿盖住 TA 的下半身
    "inside.tv": { x: 590, y: 655 },
    "inside.sofa": { x: 400, y: 905 },
    // 吃饭位：挨着两只食盆（BOWLS），站在盆的左边
    "inside.eat.1": { x: 590, y: 1090 },
    "inside.eat.2": { x: 600, y: 1195 },
  },
  courtyard: {
    // 门：视觉中心 (530,194) 在门板上，不当脚底；脚底落在台阶前的铺地上
    "courtyard.door": { x: 480, y: 630 },
    "courtyard.spot.1": { x: 430, y: 860 },
    "courtyard.spot.2": { x: 470, y: 1120 },
  },
};
export const ANCHORS: Record<HomeRoom, Record<string, StagePoint>> = {
  inside: mapPoints("inside", PLATE_ANCHORS.inside),
  courtyard: mapPoints("courtyard", PLATE_ANCHORS.courtyard),
};

/** 不认识的锚点（新布局、演示数据）：屋里在 1 号窝，院子在门前 */
const DEFAULT_ANCHOR: Record<HomeRoom, string> = { inside: "inside.rest.1", courtyard: "courtyard.door" };
/** 门：跨房间走动时在这一间从这里离开、从这里进来（屋里从画面左边出去，院子走到台阶前） */
export const DOORWAY: Record<HomeRoom, StagePoint> = {
  inside: toStage("inside", { x: -80, y: 1040 }),
  courtyard: toStage("courtyard", { x: 520, y: 590 }),
};

/**
 * 画在哪个锚点：后端给的锚点；屋里在看窗外（醒着、detail 是 watch_window）时画在 inside.window——
 * 状态写着在看窗外，画面就站在离窗最近的那一点，不站地板中间（I 10-03，2531 fx0800 走查）。事实位置照旧只读后端，这里只管画在哪。
 */
export function shownAnchor(room: HomeRoom, life: HomeLife | null): string | null {
  if (room === "inside" && life?.activity.kind === "awake" && life.activity.detail?.kind === "watch_window") return "inside.window";
  return life?.position?.anchor ?? null;
}

export function anchorPoint(room: HomeRoom, anchor: string | null | undefined): StagePoint {
  return (anchor && ANCHORS[room][anchor]) || ANCHORS[room][DEFAULT_ANCHOR[room]];
}

/**
 * 站在这一点时，TA 站姿主体画多高（舞台像素）：离镜头越近越大。
 * 屋里：地毯前沿（原图第 900 行）约 240，往前每一舞台像素大 0.15；院子：台阶前（原图第 630 行）约 190，往前每一舞台像素大 0.154。
 * 起点按原图的行换算（原来写死舞台 736、572，就是这两行在原裁切下的位置）：改了裁切（cropTop）大小也不变。
 */
const BODY_FROM = { inside: toStage("inside", { x: 0, y: 900 }).y, courtyard: toStage("courtyard", { x: 0, y: 630 }).y } as const;
export function bodyHeight(room: HomeRoom, y: number): number {
  return room === "inside" ? 240 + (y - BODY_FROM.inside) * 0.15 : 190 + (y - BODY_FROM.courtyard) * 0.154;
}

/** 走动范围：这一点附近、站得住的那一块（不走到墙上、家具里、台阶上、遮挡片的模糊边缘外） */
type Box = { x0: number; x1: number; y0: number; y1: number };
const toBox = (room: HomeRoom, b: Box): Box => {
  const a = toStage(room, { x: b.x0, y: b.y0 });
  const z = toStage(room, { x: b.x1, y: b.y1 });
  return { x0: a.x, x1: z.x, y0: a.y, y1: z.y };
};
/** 原图像素，已按占地往里收 */
const PLATE_FLOOR: Record<HomeRoom, Box[]> = {
  inside: [
    // 中央地板：r7k 地板估计多边形往里收一圈；右边让开食盆，前面最多到第 1375 行（再往前 320×640 时被底部主操作区盖住）
    { x0: 365, x1: 600, y0: 900, y1: 1375 },
    // 茶几后面地毯后沿：后边离柜脚、花盆一个占地，前边离茶几后桌腿一个占地
    { x0: 540, x1: 700, y0: 645, y1: 662 },
  ],
  courtyard: [
    // 中央铺地：r7k 安全核心往里收一圈，右边让开菜园地块，后面不上台阶
    { x0: 330, x1: 540, y0: 600, y1: 1000 },
    // 往前收窄的一截：左右都离前景栏杆植物一个占地
    { x0: 415, x1: 545, y0: 1000, y1: 1180 },
  ],
};
const FLOOR: Record<HomeRoom, Box[]> = {
  inside: PLATE_FLOOR.inside.map((b) => toBox("inside", b)),
  courtyard: PLATE_FLOOR.courtyard.map((b) => toBox("courtyard", b)),
};
const WANDER_RADIUS = { x: 150, y: 90 };

/**
 * 挡路的地方（舞台像素）：屋里是茶几桌腿那一圈（原图约 x 410–645、y 690–805）往外加 TA 的占地（左右 60、前后 25）。
 * 脚底不进这一块，就不会穿桌腿、也不会站到桌面上。院子的家具、围栏都在可走区外面，没有要绕的。
 */
export const BLOCKERS: Record<HomeRoom, Box[]> = {
  inside: [toBox("inside", { x0: 350, x1: 705, y0: 665, y1: 830 })],
  courtyard: [],
};

/** 线段 a→b 有没有从盒子里面穿过（贴着边、碰到角不算） */
export function crossesBox(b: Box, a: StagePoint, z: StagePoint): boolean {
  const dx = z.x - a.x;
  const dy = z.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const edges: [number, number][] = [
    [-dx, a.x - (b.x0 + 0.5)],
    [dx, b.x1 - 0.5 - a.x],
    [-dy, a.y - (b.y0 + 0.5)],
    [dy, b.y1 - 0.5 - a.y],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return false;
      continue;
    }
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      t0 = Math.max(t0, r);
    } else {
      if (r < t0) return false;
      t1 = Math.min(t1, r);
    }
  }
  return t0 < t1;
}

/** 一串点（从 from 出发）一共多长 */
export function pathLength(from: StagePoint, points: readonly StagePoint[]): number {
  let at = from;
  let sum = 0;
  for (const p of points) {
    sum += Math.hypot(p.x - at.x, p.y - at.y);
    at = p;
  }
  return sum;
}

/**
 * 从 from 走到 to 的路（不含 from、含 to）：直线不穿过挡路的地方就直走；穿过时贴着它的一个或相邻两个角绕过去，取最短的那条。
 */
export function routeAround(room: HomeRoom, from: StagePoint, to: StagePoint): StagePoint[] {
  const block = BLOCKERS[room].find((b) => crossesBox(b, from, to));
  if (!block) return [to];
  const corners = [
    { x: block.x0, y: block.y0 },
    { x: block.x1, y: block.y0 },
    { x: block.x1, y: block.y1 },
    { x: block.x0, y: block.y1 },
  ];
  const options: StagePoint[][] = corners.flatMap((c, i) => [[c, to], [c, corners[(i + 1) % 4], to], [c, corners[(i + 3) % 4], to]]);
  const clear = (points: StagePoint[]) => points.every((p, i) => !crossesBox(block, i ? points[i - 1] : from, p));
  const best = options.filter(clear).sort((a, b) => pathLength(from, a) - pathLength(from, b))[0];
  return best ?? [to];
}

/** 沿一串点走到全程的 share（0–1）时在哪 */
export function pointAlong(from: StagePoint, points: readonly StagePoint[], share: number): StagePoint {
  let left = Math.max(0, Math.min(1, share)) * pathLength(from, points);
  let at = from;
  for (const p of points) {
    const d = Math.hypot(p.x - at.x, p.y - at.y);
    if (left <= d && d > 0) {
      const t = left / d;
      return { x: Math.round(at.x + (p.x - at.x) * t), y: Math.round(at.y + (p.y - at.y) * t) };
    }
    left -= d;
    at = p;
  }
  return at;
}

/** 这一段直线走过哪几张遮挡片落地的那一行：在那一行把它切开（每一小段走完再决定遮挡片在上还是在下） */
export function splitAtBaselines(room: HomeRoom, from: StagePoint, to: StagePoint): StagePoint[] {
  const lines = PLATES[room].occluders.map((o) => toStage(room, { x: 0, y: o.baseline }).y);
  const cuts = lines
    .filter((y) => (from.y < y && to.y >= y) || (from.y >= y && to.y < y))
    .map((y) => (y - from.y) / (to.y - from.y))
    .sort((a, b) => a - b);
  const points: StagePoint[] = [];
  for (const t of [...new Set(cuts)]) {
    // 切点留在出发那一侧半个像素：这一小段走完之前遮挡关系还按出发那一侧算
    const y = from.y + (to.y - from.y) * t + (to.y > from.y ? -0.5 : 0.5);
    points.push({ x: Math.round((from.x + (to.x - from.x) * t) * 10) / 10, y: Math.round(y * 10) / 10 });
  }
  points.push(to);
  return points;
}

export function wanderBox(room: HomeRoom, home: StagePoint): Box {
  const floor = FLOOR[room].find((b) => home.x >= b.x0 && home.x <= b.x1 && home.y >= b.y0 && home.y <= b.y1) ?? FLOOR[room][0];
  return {
    x0: Math.max(floor.x0, home.x - WANDER_RADIUS.x),
    x1: Math.min(floor.x1, home.x + WANDER_RADIUS.x),
    y0: Math.max(floor.y0, home.y - WANDER_RADIUS.y),
    y1: Math.min(floor.y1, home.y + WANDER_RADIUS.y),
  };
}

/** 可复现的随机数（同一只宠物、同一轮挑的点一样；用例按种子核） */
export function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 一步至少走这么远（太近的挪动看着像抖） */
export const MIN_STEP = 70;

/** 在走动范围里挑下一个点：离现在的位置至少 MIN_STEP（范围太小时取离得最远的角） */
export function pickWander(random: () => number, box: Box, from: StagePoint): StagePoint {
  for (let i = 0; i < 8; i++) {
    const p = { x: Math.round(box.x0 + random() * (box.x1 - box.x0)), y: Math.round(box.y0 + random() * (box.y1 - box.y0)) };
    if (Math.hypot(p.x - from.x, p.y - from.y) >= MIN_STEP) return p;
  }
  const corners = [
    { x: box.x0, y: box.y0 }, { x: box.x1, y: box.y0 }, { x: box.x0, y: box.y1 }, { x: box.x1, y: box.y1 },
  ];
  return corners.reduce((best, p) => (Math.hypot(p.x - from.x, p.y - from.y) > Math.hypot(best.x - from.x, best.y - from.y) ? p : best));
}

/** 慢悠悠地走：每秒约 75 原图像素（约 37 CSS 像素） */
export const WALK_SPEED = 75;
export function walkMs(from: StagePoint, to: StagePoint): number {
  return walkMsFor(Math.hypot(to.x - from.x, to.y - from.y));
}
/** 走这么长一段路要多久（绕路时按整条路的长度算） */
export function walkMsFor(length: number): number {
  return Math.round(Math.min(6000, Math.max(900, (length / WALK_SPEED) * 1000)));
}

/** 停下来站一会儿：2.5–6.5 秒 */
export function pauseMs(random: () => number): number {
  return Math.round(2500 + random() * 4000);
}

/**
 * TA 此刻怎么待着（画面用）：
 * - away：不在家（不画、不留分身）；sleeping：在窝里睡；arriving：刚到家；
 * - busy：醒着、手上有具体在做的事（吃饭、看窗外……）——站在那个锚点上不走开；
 * - free：醒着、没有具体的事（日程开关关着、空档）——在附近走走停停；
 * - unknown：没有 life、或说不清——站着不动，不替 TA 编动作。
 */
export type StageMode = "away" | "sleeping" | "arriving" | "busy" | "free" | "unknown";

export function stageMode(life: HomeLife | null): StageMode {
  const kind = life?.activity.kind;
  if (kind === "away") return "away";
  if (kind === "sleeping") return "sleeping";
  if (kind === "arriving") return "arriving";
  if (kind === "awake") return life?.activity.detail ? "busy" : "free";
  return "unknown";
}

/**
 * 在做什么怎么说（醒着、有具体的事）：只说画面上看得出的那几种。
 * 底图里没有电视（watch_tv）：只说“在屋里歇着”，不说看电视；翻照片沿用已放行的那句（HomeScene ALBUM_WORD）。
 * 玩手机（phone，不是翻这趟照片的那种）说“在玩手机”（2531 R27 第 1 条，I 10-03 派、UI 同意）。
 */
export const DETAIL_WORD: Partial<Record<HomeActivityDetailKind, string>> = {
  eat: "在吃饭",
  watch_window: "在看窗外",
  watch_tv: "在屋里歇着",
  rest: "在歇着",
  stroll_courtyard: "在院子里溜达",
  phone: "在玩手机",
};

/** 白天在窗边说的那句（R27 第 1 条）。不说“晒太阳”：/home 没有天气，说不准是不是晴天（UI 6c2b 10-03 核）。 */
export const DAY_WINDOW_WORD = "在窗边往外看";

/**
 * 在做什么的说法，带上天色：看窗外在后端作息说是白天（sky 为 day）时说“在窗边往外看”，清晨、傍晚、夜里、不知道天色都说“在看窗外”。
 * 只认后端给的天色（不用 skyOf 的“没有就当白天”）：说出来的话不能比事实多。等 /home 有了天气、确定是晴天，再说“晒太阳”。
 */
export function detailWord(kind: HomeActivityDetailKind, sky: RhythmSky | null): string | undefined {
  if (kind === "watch_window" && sky === "day") return DAY_WINDOW_WORD;
  return DETAIL_WORD[kind];
}

/** 天色：后端作息给的（night / dawn / day / dusk）；没有就当白天，不按本机时钟猜 */
export function skyOf(life: HomeLife | null): RhythmSky {
  return life?.rhythm?.sky ?? "day";
}

/**
 * 此刻要不要画一段转场走动（只画镜头这一间看得到的那一段）：
 * - 起止都在这一间：从起点走到终点；
 * - 从这一间走到另一间：从起点走到门口，到了就离开画面；
 * - 从另一间走进这一间：从门口走到终点。
 * 已经走完（ends_at 已过）、还没开始、或两头都不在这一间：null。剩下的时间按真实时刻算，不加速。
 */
export type Transit = { from: StagePoint; to: StagePoint; remainingMs: number; leaves: boolean };

export function transitIn(transition: HomeTransition | null | undefined, room: HomeRoom, nowMs: number): Transit | null {
  if (!transition) return null;
  const start = Date.parse(transition.starts_at);
  const end = Date.parse(transition.ends_at);
  if (Number.isNaN(start) || Number.isNaN(end) || nowMs < start || nowMs >= end) return null;
  const share = (nowMs - start) / Math.max(1, end - start);
  const remainingMs = end - nowMs;
  const fromHere = transition.from_room === room;
  const toHere = transition.to_room === room;
  if (!fromHere && !toHere) return null;
  const from = fromHere ? anchorPoint(room, transition.from_anchor) : DOORWAY[room];
  const to = toHere ? anchorPoint(room, transition.to_anchor) : DOORWAY[room];
  // 已经走了一段：沿整条路（绕开挡路的地方）从走到的那一点接着走
  const at = pointAlong(from, routeAround(room, from, to), share);
  return { from: at, to, remainingMs, leaves: fromHere && !toHere };
}

/** Reviewed ASSET-UI-033 night RGB with the original day geometry and alpha cuts.
 * NIGHT-POLISH-02: night-v2 = I's lossy re-export of the same masters (q85, alpha identical; 1.24MB→99KB, 1.51MB→162KB); night-v1 is kept.
 * Only the explicit server sky selects this plate; sleep and the host clock do not.
 * A daytime motion overlay cannot be played over a different night plate.
 */
const NIGHT_LAYERS = "/ui-assets/HOME-LAYERS/night-v2/";
function reviewedNightPlate(day: Plate): Plate {
  const source = (url: string) => `${NIGHT_LAYERS}${url.slice(LAYERS.length, -5)}-night.webp`;
  return { ...day, src: source(day.src), motion: undefined,
    occluders: day.occluders.map(o => ({ ...o, src: source(o.src) })) };
}
const NIGHT_PLATES: Record<HomeRoom, Plate> = {
  inside: reviewedNightPlate(PLATES.inside),
  courtyard: reviewedNightPlate(PLATES.courtyard),
};
export function plateForSky(room: HomeRoom, sky: RhythmSky | null): Plate {
  return sky === "night" ? NIGHT_PLATES[room] : PLATES[room];
}

/** 一间屋白天或夜里的整套图（底图＋每一张遮挡片）：开着页面昼夜交替时要一起下好、解码好，再同一次换上（UI2-NIGHT-HOME-01）。 */
export function plateSources(room: HomeRoom, night: boolean): string[] {
  const plate = night ? NIGHT_PLATES[room] : PLATES[room];
  return [plate.src, ...plate.occluders.map((o) => o.src)];
}

/**
 * 演示用天色（UI2-NIGHT-HOME-01，照 avatar3d/flags 的 ?cat3d=1）：前端数据模式是 fixture、这份快照也来自 fixture、
 * 网址带 ?sky=night|dawn|day|dusk 才认，用来在演示数据（没有作息）里看夜景。只换画面上的天色和底图；
 * TA 在做什么、怎么说照旧只读后端作息。live 的真实宠物带了参数也不认。
 */
export const SKY_PARAM = "sky";
export function forcedSky(input: { dataMode: string; dataOrigin: string | null | undefined; value: string | null }): RhythmSky | null {
  if (input.dataMode !== "fixture" || input.dataOrigin !== "fixture") return null;
  return input.value === "night" || input.value === "dawn" || input.value === "day" || input.value === "dusk" ? input.value : null;
}
